import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Check, Tag, User } from "lucide-react";

const emptyShip = {
  full_name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
};

export default function Checkout() {
  const { items, subtotal, clear, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [shipping, setShipping] = useState({ ...emptyShip, full_name: user?.name || "" });
  const [placing, setPlacing] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("new");
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, discount, kind, value }
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Guest checkout
  const [guestEmail, setGuestEmail] = useState("");

  useEffect(() => {
    if (user) {
      api.get("/addresses").then((r) => {
        setAddresses(r.data);
        if (r.data.length > 0) {
          setSelectedAddrId(r.data[0].id);
          const a = r.data[0];
          setShipping({
            full_name: a.full_name, phone: a.phone, line1: a.line1, line2: a.line2 || "",
            city: a.city, state: a.state, pincode: a.pincode, country: a.country || "India",
          });
        }
      }).catch(() => {});
    }
  }, [user]);

  const selectAddress = (id) => {
    setSelectedAddrId(id);
    if (id === "new") {
      setShipping({ ...emptyShip, full_name: user?.name || "" });
      return;
    }
    const a = addresses.find((x) => x.id === id);
    if (a) {
      setShipping({
        full_name: a.full_name, phone: a.phone, line1: a.line1, line2: a.line2 || "",
        city: a.city, state: a.state, pincode: a.pincode, country: a.country || "India",
      });
    }
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplyingCoupon(true);
    try {
      const r = await api.get(`/coupons/apply/${couponInput.trim().toUpperCase()}`, { params: { subtotal } });
      setCoupon(r.data);
      toast.success(`Coupon ${r.data.code} applied — you saved ${formatINR(r.data.discount)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const discount = coupon?.discount || 0;
  const shippingCost = subtotal > 5000 || subtotal === 0 ? 0 : 250;
  const total = Math.max(0.01, subtotal + shippingCost - discount);

  const canProceed = (() => {
    if (!shipping.full_name || !shipping.phone || !shipping.line1 || !shipping.city || !shipping.state || !shipping.pincode) return false;
    if (!user && !guestEmail.trim()) return false;
    return true;
  })();

  const openRazorpay = (data) => {
    if (typeof window.Razorpay !== "function") {
      toast.error("Razorpay checkout could not load. Please try again.");
      return;
    }
    const options = {
      key: data.razorpay_key_id,
      amount: data.amount_paise,
      currency: data.currency,
      order_id: data.razorpay_order_id,
      name: "shopnest",
      description: `Order ${data.order_id.slice(0, 8)}`,
      prefill: { name: data.user_name, email: data.user_email, contact: shipping.phone },
      theme: { color: "#c8532e" },
      handler: async (res) => {
        try {
          await api.post("/orders/verify", {
            order_id: data.order_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          clear();
          toast.success("Payment successful");
          navigate(`/order-success/${data.order_id}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Payment verification failed");
        }
      },
      modal: { ondismiss: () => toast.info("Payment cancelled") },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const placeOrder = async () => {
    if (!canProceed || items.length === 0) return;
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
        })),
        shipping,
        coupon_code: coupon?.code || null,
      };
      if (!user) {
        payload.guest_email = guestEmail;
        payload.guest_name = shipping.full_name;
      }
      const r = await api.post("/orders/create", payload);
      const data = r.data;

      if (data.mock) {
        await api.post("/orders/verify", {
          order_id: data.order_id,
          razorpay_order_id: data.razorpay_order_id,
          razorpay_payment_id: `mock_pay_${data.order_id.slice(0, 8)}`,
          razorpay_signature: "mock_sig",
        });
        clear();
        toast.success("Payment successful (Test Mode)");
        navigate(`/order-success/${data.order_id}`);
      } else {
        openRazorpay(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen">
        <div className="max-w-[900px] mx-auto px-6 py-12 text-center">
          <div className="card p-10">
            <h1 className="font-display text-3xl font-semibold text-[#1a1d29] mb-2">Your bag is empty</h1>
            <p className="text-sm text-[#6b6b6b]">Add pieces before proceeding to checkout.</p>
            <button onClick={() => navigate("/shop")} className="btn-primary mt-6">Continue shopping</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="checkout-page">
      <div className="border-b border-[#e0d8c8]">
        <div className="max-w-[1500px] mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <div className="font-display text-2xl font-semibold">shopnest<span className="text-[#c8532e]">.</span></div>
          <h1 className="text-lg md:text-xl font-medium flex-1">Checkout <span className="text-[#6b6b6b] text-sm">({count} item{count !== 1 ? "s" : ""})</span></h1>
          <div className="flex items-center gap-1 text-sm text-[#6b6b6b]">
            <Lock className="w-4 h-4" /> Secure
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 md:px-6 py-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-8">

          {/* 1. Contact / Guest */}
          {!user && (
            <section className="border-b border-[#e0d8c8] pb-6">
              <div className="flex items-baseline gap-3 mb-4">
                <div className="w-7 h-7 rounded-full bg-[#1a1d29] text-white text-sm font-bold flex items-center justify-center">1</div>
                <h2 className="font-display text-xl font-semibold">Contact</h2>
              </div>
              <div className="pl-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Email (for order receipt)" value={guestEmail} onChange={setGuestEmail} testId="guest-email" required />
                <div className="flex flex-col justify-center text-sm text-[#6b6b6b]">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>
                      Checking out as guest.{" "}
                      <button
                        type="button"
                        onClick={() => navigate("/login", { state: { from: "/checkout" } })}
                        className="ec-link"
                        data-testid="guest-signin-link"
                      >
                        Sign in for faster checkout →
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Shipping */}
          <section className="py-6 border-b border-[#e0d8c8]">
            <div className="flex items-baseline gap-3 mb-4">
              <div className="w-7 h-7 rounded-full bg-[#1a1d29] text-white text-sm font-bold flex items-center justify-center">{user ? 1 : 2}</div>
              <h2 className="font-display text-xl font-semibold">Shipping address</h2>
            </div>

            {user && addresses.length > 0 && (
              <div className="pl-10 mb-4">
                <div className="text-sm font-semibold mb-2">Choose an address</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {addresses.map((a) => (
                    <label
                      key={a.id}
                      className={`border rounded-md p-3 cursor-pointer text-sm ${selectedAddrId === a.id ? "border-[#c8532e] ring-2 ring-[#c8532e]/30 bg-[#f3e0d8]" : "border-[#e0d8c8]"}`}
                      data-testid={`address-option-${a.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          className="mt-1 accent-[#c8532e]"
                          checked={selectedAddrId === a.id}
                          onChange={() => selectAddress(a.id)}
                        />
                        <div>
                          <div className="font-semibold">{a.label} · {a.full_name}</div>
                          <div className="text-[#6b6b6b] mt-0.5">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode}</div>
                          <div className="text-[#6b6b6b]">{a.phone}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                  <label
                    className={`border rounded-md p-3 cursor-pointer text-sm flex items-center justify-center ${selectedAddrId === "new" ? "border-[#c8532e] ring-2 ring-[#c8532e]/30 bg-[#f3e0d8]" : "border-[#e0d8c8]"}`}
                    data-testid="address-option-new"
                  >
                    <input
                      type="radio"
                      className="mr-2 accent-[#c8532e]"
                      checked={selectedAddrId === "new"}
                      onChange={() => selectAddress("new")}
                    />
                    Use a new address
                  </label>
                </div>
              </div>
            )}

            <div className="pl-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" value={shipping.full_name} onChange={(v) => setShipping({ ...shipping, full_name: v })} testId="ship-name" required />
              <Field label="Phone Number" value={shipping.phone} onChange={(v) => setShipping({ ...shipping, phone: v })} testId="ship-phone" required />
              <Field label="Address Line 1" value={shipping.line1} onChange={(v) => setShipping({ ...shipping, line1: v })} full testId="ship-line1" required />
              <Field label="Address Line 2 (optional)" value={shipping.line2} onChange={(v) => setShipping({ ...shipping, line2: v })} full testId="ship-line2" />
              <Field label="City" value={shipping.city} onChange={(v) => setShipping({ ...shipping, city: v })} testId="ship-city" required />
              <Field label="State" value={shipping.state} onChange={(v) => setShipping({ ...shipping, state: v })} testId="ship-state" required />
              <Field label="Pincode" value={shipping.pincode} onChange={(v) => setShipping({ ...shipping, pincode: v })} testId="ship-pincode" required />
              <Field label="Country" value={shipping.country} onChange={(v) => setShipping({ ...shipping, country: v })} testId="ship-country" />
            </div>
          </section>

          {/* Payment */}
          <section className="py-6 border-b border-[#e0d8c8]">
            <div className="flex items-baseline gap-3 mb-4">
              <div className="w-7 h-7 rounded-full bg-[#1a1d29] text-white text-sm font-bold flex items-center justify-center">{user ? 2 : 3}</div>
              <h2 className="font-display text-xl font-semibold">Payment method</h2>
            </div>
            <div className="pl-10">
              <label className="flex items-start gap-3 border border-[#c8532e] bg-[#f3e0d8] rounded-md p-4">
                <input type="radio" checked readOnly className="mt-1 accent-[#c8532e]" />
                <div>
                  <div className="font-semibold">Razorpay</div>
                  <div className="text-sm text-[#6b6b6b] mt-1">
                    Pay securely using UPI · Credit/Debit cards · Netbanking · Wallets
                  </div>
                  <div className="text-xs text-[#8b3a3a] mt-2">100% secure payments</div>
                </div>
              </label>
            </div>
          </section>

          {/* Review */}
          <section className="py-6">
            <div className="flex items-baseline gap-3 mb-4">
              <div className="w-7 h-7 rounded-full bg-[#1a1d29] text-white text-sm font-bold flex items-center justify-center">{user ? 3 : 4}</div>
              <h2 className="font-display text-xl font-semibold">Review items</h2>
            </div>
            <div className="pl-10 space-y-4">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.size}-${it.color}`} className="flex gap-4">
                  <div className="w-20 h-24 bg-[#faf7f2] border border-[#e0d8c8] rounded-md overflow-hidden shrink-0">
                    {it.image && <img src={it.image} className="w-full h-full object-cover" alt="" />}
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-[#6b6b6b]">
                      {it.size ? `Size ${it.size}` : ""}{it.size && it.color ? " · " : ""}{it.color ? `Colour ${it.color}` : ""}
                    </div>
                    <div className="text-[#6b6b6b]">Qty {it.quantity}</div>
                  </div>
                  <div className="text-sm font-semibold">{formatINR(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="col-span-12 md:col-span-4">
          <div className="card p-5 sticky top-4">
            {/* Coupon */}
            <div className="pb-4 border-b border-[#e0d8c8]">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Tag className="w-4 h-4" /> Have a promo code?</div>
              {coupon ? (
                <div className="flex items-center justify-between bg-[#f3e0d8] border border-[#c8532e]/40 rounded-md p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#3a6a3a]" />
                    <span className="font-semibold">{coupon.code}</span>
                    <span className="text-[#6b6b6b] text-xs">−{formatINR(coupon.discount)}</span>
                  </div>
                  <button className="text-xs ec-link" onClick={removeCoupon} data-testid="coupon-remove">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    placeholder="e.g. WELCOME10"
                    className="flex-1 h-10 bg-white border border-[#e0d8c8] rounded-md outline-none px-3 text-sm"
                    data-testid="coupon-input"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applyingCoupon}
                    className="btn-outline text-sm px-4"
                    data-testid="coupon-apply"
                  >
                    {applyingCoupon ? "…" : "Apply"}
                  </button>
                </div>
              )}
            </div>

            <div className="pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Items ({count}):</span>
                <span>{formatINR(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping:</span>
                <span>{shippingCost === 0 ? "FREE" : formatINR(shippingCost)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-[#3a6a3a]">
                  <span>Coupon discount:</span>
                  <span>−{formatINR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-[#e0d8c8] pt-3 mt-3 items-baseline">
                <span className="font-semibold">Order total:</span>
                <span className="font-display text-2xl font-semibold" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
            </div>

            <button
              onClick={placeOrder}
              disabled={!canProceed || placing}
              className="btn-primary w-full text-sm font-semibold mt-5"
              data-testid="place-order-button"
            >
              {placing ? "Processing…" : `Place order · ${formatINR(total)}`}
            </button>
            <p className="text-xs text-[#6b6b6b] mt-2">
              By placing your order, you agree to shopnest's Terms and Privacy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, full, testId, required }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-[#1a1d29]">{label}{required && <span className="text-[#b52929]"> *</span>}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 bg-white border border-[#e0d8c8] focus:border-[#c8532e] focus:ring-2 focus:ring-[#c8532e]/30 rounded-md outline-none px-3 text-sm"
        data-testid={testId}
      />
    </label>
  );
}
